/**
 * WebSocket Transport Implementation.
 *
 * Provides bidirectional WebSocket-based streaming with automatic
 * reconnection, RPC support, and fallback to SSE.
 */

import type {
  ChatTransport,
  ChatTransportConfig,
  SendMessagesRequest,
  ReconnectRequest,
  ToolOutputRequest,
  StreamEvent,
  TransportConnectionState,
} from './ChatTransport';
import { resolveHeaders } from './ChatTransport';
import type { RpcRequest, RpcNotification, RpcResponse } from '../rpc/types';

/**
 * WebSocket transport configuration.
 */
export interface WebSocketTransportConfig extends ChatTransportConfig {
  /** WebSocket endpoint path (default: '/ws') */
  wsEndpoint?: string;
  /** HTTP stop endpoint path for fallback (default: '/chat/stop') */
  stopEndpoint?: string;
  /** Reconnection attempts (default: 3) */
  maxReconnectAttempts?: number;
  /** Base reconnect delay in ms (default: 1000) */
  reconnectDelay?: number;
  /** Heartbeat interval in ms (default: 30000) */
  heartbeatInterval?: number;
  /** Callback when connection state changes */
  onConnectionStateChange?: (state: TransportConnectionState) => void;
  /** Callback for handling incoming RPC requests (expects response) */
  onRpcRequest?: (
    request: RpcRequest,
    sendResponse: (response: RpcResponse) => void,
  ) => void;
  /** Callback for handling incoming RPC notifications (fire-and-forget, no response) */
  onRpcNotification?: (notification: RpcNotification) => void;
  /** Callback for handling incoming RPC responses (for outbound RPC calls) */
  onRpcResponse?: (response: RpcResponse) => void;
  /** Spec 048/089: server capability advertisement (delta-protocol probe). */
  onServerCapabilities?: (supports: string[]) => void;
}

/**
 * WebSocket-based chat transport with RPC support.
 */
export class WebSocketTransport implements ChatTransport {
  private config: WebSocketTransportConfig;
  private ws: WebSocket | null = null;
  private connectionState: TransportConnectionState = 'disconnected';
  private reconnectAttempts = 0;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private messageResolver: ((event: StreamEvent) => void) | null = null;
  private messageQueue: StreamEvent[] = [];
  private streamActive = false;
  private currentChatId: string | null = null;
  private currentUserId: string | null = null;

  constructor(config: WebSocketTransportConfig) {
    this.config = {
      wsEndpoint: '/ws',
      stopEndpoint: '/chat/stop',
      maxReconnectAttempts: 3,
      reconnectDelay: 1000,
      heartbeatInterval: 30000,
      ...config,
    };
  }

  /**
   * Connect to the WebSocket server.
   */
  async connect(chatId: string, userId: string, organizationId?: string): Promise<void> {
    if (this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    this.currentChatId = chatId;
    this.currentUserId = userId;
    this.setConnectionState('connecting');

    return new Promise((resolve, reject) => {
      const wsUrl = this.buildWebSocketUrl(chatId, userId, organizationId);
      this.ws = new WebSocket(wsUrl);

      // Track if connection attempt is still pending
      let connectionPending = true;

      this.ws.onopen = () => {
        connectionPending = false;
        this.setConnectionState('connected');
        this.reconnectAttempts = 0;
        this.startHeartbeat();
        resolve();
      };

      this.ws.onclose = () => {
        // Only handle disconnect if connection was established
        if (!connectionPending) {
          this.handleDisconnect();
        }
      };

      this.ws.onerror = () => {
        if (connectionPending) {
          connectionPending = false;
          // Clean up handlers to prevent onclose from triggering reconnect
          if (this.ws) {
            this.ws.onopen = null;
            this.ws.onclose = null;
            this.ws.onerror = null;
            this.ws.onmessage = null;
            this.ws.close();
            this.ws = null;
          }
          this.setConnectionState('error');
          reject(new Error('WebSocket connection failed'));
        }
      };

      this.ws.onmessage = (event) => {
        this.handleMessage(event);
      };
    });
  }

  /**
   * Chat must use SSE transport, not WebSocket.
   *
   * WebSocket is for RPC (browser tools) and control messages only.
   * This method exists to satisfy the ChatTransport interface but
   * must never be called — use SSETransport for chat.
   */
  async *sendMessages(
    _request: SendMessagesRequest,
  ): AsyncIterable<StreamEvent> {
    throw new Error(
      'Chat must use SSE transport. WebSocket is for RPC only.',
    );
  }

  /**
   * WebSocket transport doesn't support stream reconnection currently.
   */
  async reconnectToStream(
    _request: ReconnectRequest,
  ): Promise<AsyncIterable<StreamEvent> | null> {
    return null;
  }

  /**
   * Stop the current stream.
   */
  async stop(chatId: string, userId: string): Promise<void> {
    this.streamActive = false;

    // Send stop via WebSocket if connected
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(
        JSON.stringify({
          type: 'chat-stop',
          chatId,
        }),
      );
    }

    // Also send via HTTP as fallback
    const url = `${this.config.baseUrl}${this.config.stopEndpoint}`;
    try {
      await fetch(url, {
        method: 'POST',
        headers: resolveHeaders(this.config, {
          'Content-Type': 'application/json',
        }),
        credentials: this.config.credentials,
        body: JSON.stringify({
          session_id: chatId,
          user_id: userId,
        }),
      });
    } catch {
      // Ignore stop request failures
    }
  }

  /**
   * Submit tool output via WebSocket.
   */
  async addToolOutput(request: ToolOutputRequest): Promise<void> {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket not connected');
    }

    this.ws.send(
      JSON.stringify({
        type: 'tool-output',
        toolCallId: request.toolCallId,
        output: request.output,
        isError: request.isError,
      }),
    );
  }

  /**
   * Get the current connection state.
   */
  getConnectionState(): TransportConnectionState {
    return this.connectionState;
  }

  /**
   * Check if connected.
   */
  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Send a raw JSON message over the WebSocket.
   *
   * Used for notifications like tools-changed that don't fit
   * the standard request/response pattern.
   */
  send(message: Record<string, unknown>): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('WebSocket not connected, cannot send message');
      return;
    }
    this.ws.send(JSON.stringify(message));
  }

  /**
   * Get the underlying WebSocket instance.
   */
  get socket(): WebSocket | null {
    return this.ws;
  }

  /**
   * Clean up resources.
   */
  dispose(): void {
    this.streamActive = false;
    this.stopHeartbeat();
    this.ws?.close();
    this.ws = null;
    this.setConnectionState('disconnected');
  }

  // Private methods

  private buildWebSocketUrl(chatId: string, userId: string, organizationId?: string): string {
    const baseUrl = this.config.baseUrl.replace(/^http/, 'ws');
    let url = `${baseUrl}${this.config.wsEndpoint}/${chatId}?user_id=${encodeURIComponent(userId)}`;
    if (organizationId) {
      url += `&organization_id=${encodeURIComponent(organizationId)}`;
    }
    // The orchestrator WS handler decodes user_id/organization_id from the
    // session_token JWT and refuses the connection without it (browser-tools
    // discovery, canvas RPC). Forward the login JWT as session_token — same
    // name VoiceTransport uses — since WS upgrades can't carry auth headers.
    const sessionToken =
      typeof localStorage !== 'undefined' ? localStorage.getItem('cxs_token') : null;
    if (sessionToken) {
      url += `&session_token=${encodeURIComponent(sessionToken)}`;
    }
    return url;
  }

  private setConnectionState(state: TransportConnectionState): void {
    this.connectionState = state;
    this.config.onConnectionStateChange?.(state);
  }

  private handleMessage(event: MessageEvent): void {
    try {
      const data = JSON.parse(event.data) as Record<string, unknown>;

      // Handle RPC requests from server (expects response)
      if (data.type === 'rpc-request') {
        this.handleRpcRequest(data as unknown as RpcRequest);
        return;
      }

      // Handle RPC notifications from server (fire-and-forget, no response)
      if (data.type === 'rpc-notification') {
        this.handleRpcNotification(data as unknown as RpcNotification);
        return;
      }

      // Handle RPC responses (for outbound calls)
      if (data.type === 'rpc-response') {
        if (this.config.onRpcResponse) {
          this.config.onRpcResponse(data as unknown as RpcResponse);
        }
        return;
      }

      // Spec 048/089: intercept the server's capability advertisement so it
      // never reaches the app-level stream queue.
      if (data.type === 'capabilities') {
        const supports = Array.isArray(data.supports)
          ? (data.supports as unknown[]).filter(
              (s): s is string => typeof s === 'string',
            )
          : [];
        this.config.onServerCapabilities?.(supports);
        return;
      }

      // Queue as stream event
      const streamEvent = data as StreamEvent;
      if (this.messageResolver) {
        this.messageResolver(streamEvent);
        this.messageResolver = null;
      } else {
        this.messageQueue.push(streamEvent);
      }
    } catch {
      // Ignore malformed messages
    }
  }

  private handleRpcRequest(request: RpcRequest): void {
    if (!this.config.onRpcRequest) return;

    this.config.onRpcRequest(request, (response) => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify(response));
      }
    });
  }

  /**
   * Handle RPC notification (fire-and-forget).
   * Per JSON-RPC 2.0 spec, notifications MUST NOT receive a reply.
   */
  private handleRpcNotification(notification: RpcNotification): void {
    if (!this.config.onRpcNotification) return;
    this.config.onRpcNotification(notification);
  }

  private handleDisconnect(): void {
    this.stopHeartbeat();

    if (this.streamActive) {
      this.attemptReconnect();
    } else {
      // Not streaming — don't eagerly reconnect. The WebSocketContext
      // will re-establish the connection when it's needed (e.g., on
      // next chat message or explicit connect() call).
      this.setConnectionState('disconnected');
    }
  }

  private async attemptReconnect(): Promise<void> {
    if (
      this.reconnectAttempts >= (this.config.maxReconnectAttempts || 3) ||
      !this.currentChatId ||
      !this.currentUserId
    ) {
      this.setConnectionState('error');
      this.streamActive = false;
      return;
    }

    this.setConnectionState('reconnecting');
    this.reconnectAttempts++;

    const delay = (this.config.reconnectDelay || 1000) * this.reconnectAttempts;
    await new Promise((resolve) => setTimeout(resolve, delay));

    try {
      await this.connect(this.currentChatId, this.currentUserId);
    } catch {
      await this.attemptReconnect();
    }
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'heartbeat' }));
      }
    }, this.config.heartbeatInterval || 30000);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private waitForEvent(): Promise<StreamEvent | null> {
    // Return queued event if available
    if (this.messageQueue.length > 0) {
      return Promise.resolve(this.messageQueue.shift()!);
    }

    // Wait for next event
    return new Promise((resolve) => {
      if (!this.streamActive) {
        resolve(null);
        return;
      }

      // Timeout after 60 seconds
      const timeoutId = setTimeout(() => {
        this.messageResolver = null;
        resolve(null);
      }, 60000);

      // Wrap resolve to clear timeout when event arrives
      this.messageResolver = (event) => {
        clearTimeout(timeoutId);
        resolve(event);
      };
    });
  }
}
